import React, { useState } from 'react';
import { Box, Typography, List, ListItem, ListItemText, ListItemIcon, ListItemButton, Switch, Divider, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Snackbar, Alert } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import NotificationsIcon from '@mui/icons-material/Notifications';
import StorageIcon from '@mui/icons-material/Storage';
import InfoIcon from '@mui/icons-material/Info';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import CodeIcon from '@mui/icons-material/Code';
import LoginIcon from '@mui/icons-material/Login';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import { useAuth } from '../context/AuthContext';
import LoginForm from '../components/auth/LoginForm';
import { useTheme } from '../context/ThemeContext';
import SettingsIcon from '@mui/icons-material/Settings';
import LightModeIcon from '@mui/icons-material/LightMode';
import AssessmentIcon from '@mui/icons-material/Assessment';

const SettingsPage = () => {
  const { user, login, logout, hasPermission, isAuthenticated, isSuperUser } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [notifications, setNotifications] = useState(true);
  const [confirmClearData, setConfirmClearData] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const navigate = useNavigate();
  
  const [settings, setSettings] = React.useState({
    darkMode: false,
    notifications: true,
    offlineMode: false
  });

  const handleChange = (setting) => (event) => {
    setSettings({
      ...settings,
      [setting]: event.target.checked
    });
  };

  const handleLogin = (userData) => {
    login(userData.username, userData.password, userData.role);
    setLoginDialogOpen(false);
  };

  const handleLogout = () => {
    logout();
  };

  const openLoginDialog = () => {
    setLoginDialogOpen(true);
  };

  const closeLoginDialog = () => {
    setLoginDialogOpen(false);
  };

  const handleToggleNotifications = () => {
    setNotifications(prev => !prev);
    showSnackbar('Notification settings updated', 'success');
  };

  const handleClearData = () => {
    setConfirmClearData(true);
  };

  const confirmClearDataAction = () => {
    // In a real app, this would clear local storage or call API
    localStorage.removeItem('treeRecords');
    setConfirmClearData(false);
    showSnackbar('Application data cleared successfully', 'success');
  };

  const cancelClearData = () => {
    setConfirmClearData(false);
  };

  const navigateToAdmin = () => {
    navigate('/admin');
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Check if user has admin or superuser permissions
  const hasAdminAccess = hasPermission('is_staff') || isSuperUser();

  return (
    <Box sx={{ p: 2, mb: 8 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SettingsIcon sx={{ mr: 1 }} />
        Settings
      </Typography>
      
      {/* General Settings */}
      <Paper elevation={2} sx={{ mt: 2 }}>
        <List>
          <ListItem>
            <ListItemIcon>
              {darkMode ? <DarkModeIcon /> : <LightModeIcon />}
            </ListItemIcon>
            <ListItemText 
              primary="Dark Mode" 
              secondary="Toggle dark/light theme"
            />
            <Switch
              edge="end"
              checked={darkMode}
              onChange={toggleDarkMode}
              color="primary"
            />
          </ListItem>
          
          <Divider />
          
          <ListItem>
            <ListItemIcon>
              <NotificationsIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Notifications" 
              secondary="Enable or disable app notifications"
            />
            <Switch 
              edge="end" 
              checked={notifications} 
              onChange={handleToggleNotifications}
            />
          </ListItem>
          
          <Divider />
          
          <ListItem>
            <ListItemIcon>
              <StorageIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Clear App Data" 
              secondary="Remove all tree records and cached data"
            />
            <Button 
              variant="outlined" 
              color="error" 
              onClick={handleClearData}
              size="small"
            >
              Clear Data
            </Button>
          </ListItem>
        </List>
      </Paper>
      
      {/* About Section */}
      <Paper elevation={2} sx={{ mt: 3 }}>
        <List>
          <ListItemButton>
            <ListItemIcon>
              <InfoIcon />
            </ListItemIcon>
            <ListItemText 
              primary="About" 
              secondary="App information and version"
            />
          </ListItemButton>
        </List>
      </Paper>
      
      {/* Admin Access - Only visible for staff users */}
      {hasAdminAccess && (
        <Paper elevation={2} sx={{ mt: 3, bgcolor: 'rgba(46, 125, 50, 0.05)' }}>
          <List>
            <ListItem>
              <ListItemIcon>
                <SupervisorAccountIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Admin Dashboard" 
                secondary="Access tree management and reports" 
              />
              <Button 
                component={Link}
                to="/admin"
                variant="outlined" 
                color="primary"
              >
                Access
              </Button>
            </ListItem>

            {/* Client Management Section */}
            <Divider />
            <ListItem>
              <ListItemIcon>
                <PeopleIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Client Management" 
                secondary="Manage client profiles and plantation data" 
              />
              <Button 
                component={Link}
                to="/admin/clients"
                variant="outlined" 
                color="primary"
              >
                Manage Clients
              </Button>
            </ListItem>
          </List>
        </Paper>
      )}
      
      {/* Backend Access - Only visible for superusers */}
      {isSuperUser() && (
        <Paper elevation={2} sx={{ mt: 3, bgcolor: 'rgba(211, 47, 47, 0.05)' }}>
          <List>
            <ListItem>
              <ListItemIcon>
                <CodeIcon color="error" />
              </ListItemIcon>
              <ListItemText 
                primary="Backend Management" 
                secondary="Access system administration tools (Developer/IT Only)" 
              />
              <Button 
                component={Link}
                to="/backend-management"
                variant="outlined" 
                color="error"
              >
                Access
              </Button>
            </ListItem>
          </List>
        </Paper>
      )}
      
      {/* Authentication Section */}
      <Paper elevation={2} sx={{ mt: 3 }}>
        <List>
          {!isAuthenticated() ? (
            <>
              <ListItem>
                <ListItemIcon>
                  <LoginIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Worker Login" 
                  secondary="Login to access worker features"
                />
                <Button 
                  variant="outlined" 
                  onClick={openLoginDialog}
                  size="small"
                >
                  Login
                </Button>
              </ListItem>
              
              <Divider />
              
              <ListItem>
                <ListItemIcon>
                  <BusinessIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Client Login" 
                  secondary="Login to access client plantation dashboard"
                />
                <Button 
                  variant="outlined" 
                  component={Link}
                  to="/customer-login"
                  size="small"
                  color="secondary"
                >
                  Client Portal
                </Button>
              </ListItem>
            </>
          ) : (
            <ListItem>
              <ListItemIcon>
                <LogoutIcon color="error" />
              </ListItemIcon>
              <ListItemText 
                primary={`Logged in as ${user.username}`} 
                secondary={`Role: ${user.role}`} 
              />
              <Button 
                variant="outlined" 
                color="error" 
                onClick={handleLogout}
              >
                Logout
              </Button>
            </ListItem>
          )}
        </List>
      </Paper>
      
      {/* Login Dialog */}
      <Dialog open={loginDialogOpen} onClose={closeLoginDialog}>
        <DialogTitle>Login</DialogTitle>
        <DialogContent>
          <LoginForm onLogin={handleLogin} />
        </DialogContent>
      </Dialog>
      
      {/* Confirm Clear Data Dialog */}
      <Dialog
        open={confirmClearData}
        onClose={cancelClearData}
      >
        <DialogTitle>Clear Application Data?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove all tree records and reset the application data. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelClearData} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmClearDataAction} color="error">
            Clear Data
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={5000} 
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage; 